import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { useSessionStore, waitForHydration } from '@/stores/sessionStore'
import { usePOSStore } from '@/stores/posStore'
import { useUIStore } from '@/stores/uiStore'
import { useRegisters, useBackfillSelectedStore } from '@/api/stores'
import { Monitor, ChevronLeft, Leaf, Check, AlertCircle } from 'lucide-react'
import { AuthUserBadge } from '@/components/AuthUserBadge'
import type { Register } from '@/types'

type SearchParams = { storeId?: number }

export const Route = createFileRoute('/select-register')({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    storeId: search.storeId ? Number(search.storeId) : undefined,
  }),
  beforeLoad: async () => {
    await waitForHydration()
    const { selectedStore, backendUrl, sessionToken } = useSessionStore.getState()

    if (!backendUrl) {
      throw redirect({ to: '/connect' })
    }

    // Must have a session (manager/store login) first
    if (!sessionToken) {
      throw redirect({ to: '/auth' })
    }

    // Store must be picked before we can list its registers. /select-store
    // does the picking (auto-skips for single-store accounts).
    if (!selectedStore) {
      throw redirect({ to: '/select-store' })
    }
  },
  component: SelectRegisterPage,
})

function SelectRegisterPage() {
  const navigate = useNavigate()
  const { selectedStore, selectedRegister, isAuthenticated, setRegister } = useSessionStore()
  const authType = useSessionStore((s) => s.authType)
  const accessibleStores = useSessionStore((s) => s.accessibleStores)
  const { clearCart } = usePOSStore()

  useBackfillSelectedStore()

  // The API client's clerk-session recovery (client.ts
  // recoverDeadClerkSession) lands here via a hard navigation after
  // clearing a clerk grant the BE stopped accepting. A toast fired
  // before that navigation wouldn't survive the reload, so the reason
  // rides sessionStorage and is surfaced here — the graceful half of
  // the recovery.
  useEffect(() => {
    let reason: string | null = null
    try {
      reason = sessionStorage.getItem('waio.clerk_recovery_reason')
      if (reason) sessionStorage.removeItem('waio.clerk_recovery_reason')
    } catch {
      // Storage unavailable — nothing to surface.
    }
    if (reason) {
      useUIStore.getState().showToast({
        type: 'warning',
        title: 'PIN sign-in needed',
        message:
          'Your PIN session ended and could not be resumed automatically. '
          + 'Pick your register and sign in again — nothing on the server was lost.',
        duration: 10000,
      })
    }
  }, [])

  const currentStoreId = selectedStore?.id ?? null
  const storeName = selectedStore?.name

  // Fetch registers from API
  const { data: registers = [], isLoading, error, refetch } = useRegisters(currentStoreId, storeName)

  // Auto-skip the picker when the store only has one register — match
  // the single-store auto-pick in /select-store. /login is gated on
  // sessionToken + selectedRegister, both of which are set here, so
  // the cashier lands straight on the PIN screen.
  const autoPickedRef = useRef(false)
  useEffect(() => {
    if (autoPickedRef.current) return
    if (isLoading) return
    if (registers.length !== 1) return
    autoPickedRef.current = true
    setRegister(registers[0], clearCart)
    navigate({ to: '/login', replace: true })
  }, [isLoading, registers, setRegister, clearCart, navigate])

  const handleSelectRegister = (register: Register) => {
    setRegister(register, clearCart)
    navigate({ to: '/login' })
  }

  // Mid-session register switch from /pos: back returns to /pos.
  // Otherwise back is only meaningful when there's another store to
  // switch to — managers with a single accessible store and API-key
  // sessions (always single-store) have nowhere useful to go, so the
  // button is hidden in those cases.
  const isMidSessionFromPos = isAuthenticated && !!selectedRegister
  const canPickAnotherStore = authType === 'manager' && (accessibleStores?.length ?? 0) > 1
  const showBack = isMidSessionFromPos || canPickAnotherStore

  const handleBack = () => {
    if (isMidSessionFromPos) {
      navigate({ to: '/pos' })
      return
    }
    navigate({ to: '/select-store' })
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          {showBack && (
            <button
              onClick={handleBack}
              className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
          )}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
              <Leaf className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-gray-900">WAIO POS</h1>
              <p className="text-sm text-gray-500">{selectedStore?.name ?? 'Select Register'}</p>
            </div>
          </div>
          <AuthUserBadge />
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 p-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-2xl mb-4">
              <Monitor className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Select a Register</h2>
            <p className="text-gray-500 mt-2">Choose which terminal you'll be using</p>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-2 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                  <div className="w-12 h-12 bg-gray-200 rounded-xl mb-3" />
                  <div className="h-5 bg-gray-200 rounded w-2/3 mb-2" />
                  <div className="h-4 bg-gray-100 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <h3 className="font-semibold text-red-900 mb-1">Failed to load registers</h3>
              <p className="text-sm text-red-600 mb-4">
                {error instanceof Error ? error.message : 'Unable to connect to server'}
              </p>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium cursor-pointer"
              >
                Try Again
              </button>
            </div>
          ) : registers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No registers available for this store</p>
            </div>
          ) : registers.length === 1 ? (
            <div className="text-center py-12 text-gray-500 text-sm">Selecting register…</div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {registers.map(register => {
                const isSelected = selectedRegister?.id === register.id

                return (
                  <button
                    key={register.id}
                    onClick={() => handleSelectRegister(register)}
                    className={`relative bg-white rounded-xl p-4 flex flex-col items-start text-left transition-all hover:ring-2 hover:ring-green-500 hover:ring-offset-2 cursor-pointer ${isSelected ? 'ring-2 ring-green-500 ring-offset-2' : ''
                      }`}
                  >
                    {isSelected && (
                      <div className="absolute top-3 right-3 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 bg-green-100">
                      <Monitor className="w-6 h-6 text-green-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900">{register.name}</h3>
                    <p className="text-sm text-gray-500 mt-0.5">ID: {register.terminal_id || register.id}</p>
                    <div className="flex items-center gap-1.5 mt-2">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-xs text-gray-500">Available</span>
                    </div>
                  </button>
                )
              })}
            </div>
          )}

          {/* Quick tip */}
          <div className="mt-8 p-4 bg-green-50 rounded-xl border border-green-100">
            <p className="text-sm text-green-800">
              <strong>Tip:</strong> You can change registers at any time from the header menu in the POS.
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}
